<template>
  <div ref="modal" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Allocate Time to Tasks</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
          ></button>
        </div>

        <div class="modal-body">
          <div class="mb-3">
            <h6 class="mb-3">Tasks</h6>

            <div
              v-for="(task, index) in taskList"
              :key="task.id"
              class="row align-items-center mb-3 border-bottom pb-2"
            >
              <div class="col-md-6">
                <span class="fw-medium">{{ task.name }}</span>
              </div>
              <div class="col-md-4">
                <div class="input-group input-group-sm">
                  <input
                    type="number"
                    min="0"
                    class="form-control"
                    v-model.number="task.minutes"
                  />
                  <span class="input-group-text">mins</span>
                </div>
              </div>
              <div class="col-md-2 text-end">
                <button
                  class="btn btn-outline-danger btn-sm"
                  @click="removeTask(index)"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>

          <!-- Add Task -->
          <div class="row mb-4">
            <div class="col-md-8">
              <v-select
                v-model="selectedTask"
                :options="availableTasks"
                label="name"
                placeholder="Select a task"
                :reduce="(t) => t.id"
              />
            </div>
            <div class="col-md-4">
              <button
                class="btn btn-outline-primary w-100"
                @click="addTask"
                :disabled="!selectedTask"
              >
                Add Task
              </button>
            </div>
          </div>

          <!-- Visualization -->
          <div class="bg-light p-3 rounded">
            <div class="d-flex justify-content-between mb-1">
              <small class="fw-semibold">Allocation Overview</small>
              <small>{{ allocatedPercent }}% Allocated</small>
            </div>

            <div class="progress" style="height: 22px">
              <div
                v-for="(task, i) in taskList"
                :key="'bar-' + task.id"
                class="progress-bar"
                :class="barColor(i)"
                :style="{ width: taskPercent(task) + '%' }"
              >
                {{ taskPercent(task) }}%
              </div>

              <div
                class="progress-bar bg-light text-dark"
                :style="{ width: remainingPercent + '%' }"
              >
                {{ remainingPercent }}% Free
              </div>
            </div>

            <div class="d-flex justify-content-between mt-3">
              <span>Total Allocated:</span>
              <span
                >{{ formatTime(totalAllocated * 60) }} ({{
                  allocatedPercent
                }}%)</span
              >
            </div>
            <div class="d-flex justify-content-between">
              <span>Not Allocated:</span>
              <span :class="remainingClass">
                {{ formatTime(remainingTime * 60) }} ({{ remainingPercent }}%)
              </span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="remainingTime < 0"
            @click="saveAllocation"
          >
            Save Allocation
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const bootstrap = require("bootstrap");
import http from "../../modules/http";

export default {
  name: "TimeAllocatorModal",
  props: {
    modelValue: Object,
  },
  emits: ["update:modelValue"],
  data() {
    return {
      myModal: undefined,
      newTask: "",
      selectedTask: null,
      taskList: [],
      availableTasks: [],
    };
  },
  computed: {
    totalTime() {
      return this.modelValue?.duration ?? 0;
    },
    totalAllocated() {
      return this.taskList.reduce((sum, t) => sum + (t.minutes || 0), 0) * 60;
    },

    remainingTime() {
      return this.totalTime - this.totalAllocated;
    },

    allocatedPercent() {
      if (this.totalTime === 0) return 0;
      return Math.min(
        100,
        Math.round((this.totalAllocated / this.totalTime) * 100)
      );
    },

    remainingPercent() {
      return Math.max(0, 100 - this.allocatedPercent);
    },

    remainingClass() {
      return this.remainingTime < 0
        ? "text-danger fw-semibold"
        : "text-success fw-semibold";
    },
  },

  methods: {
    showModal(bucketId = null) {
      if (this.myModal === undefined) {
        this.myModal = new bootstrap.Modal(this.$refs.modal, {
          backdrop: "static",
        });
      }
      // Reset tasks before loading new ones
      this.availableTasks = [];
      this.taskList = [];

      // Use passed bucketId or fall back to modelValue
      const id = bucketId || this.modelValue?.work_bucket_id;
      if (id) {
        this.load(id);
      }
      this.myModal.show();
    },
    load(bucketId) {
      if (bucketId) {
        http.fetch(`/work/buckets/${bucketId}/lists`).then((data) => {
          this.availableTasks = [];
          if (data) {
            data.forEach((group) => {
              if (group.workTasks) {
                group.workTasks.forEach((task) => {
                  this.availableTasks.push({
                    id: task.id,
                    name: `${group.name} - ${task.name}`,
                    work_task_group_id: group.id,
                  });
                });
              }
            });
          }
          // Initialize task list with existing allocations
          this.taskList = this.modelValue?.allocations || [];
        });
      }
    },
    formatTime(seconds, secunds = false) {
      if (seconds >= 3600) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (secunds) {
          const remainingSeconds = seconds % 60;
          return `${hours}:${minutes
            .toString()
            .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
        }
        return `${hours}:${minutes.toString().padStart(2, "0")}`;
      } else if (seconds >= 60) {
        const minutes = Math.floor(seconds / 60);
        return `${minutes} min`;
      } else {
        return `${seconds} s`;
      }
    },
    barColor(index) {
      const palette = [
        "bg-primary",
        "bg-success",
        "bg-warning",
        "bg-info",
        "bg-danger",
        "bg-secondary",
        "bg-dark",
      ];
      return palette[index % palette.length];
    },
    taskPercent(task) {
      if (!task.minutes || this.totalTime === 0) return 0;
      return Math.round(((task.minutes * 60) / this.totalTime) * 100);
    },

    addTask() {
      if (!this.selectedTask) return;

      const task = this.availableTasks.find((t) => t.id === this.selectedTask);
      if (task) {
        this.taskList.push({
          id: task.id,
          name: task.name,
          work_task_group_id: task.work_task_group_id,
          minutes: 0,
        });
        this.selectedTask = null;
      }
    },

    removeTask(index) {
      this.taskList.splice(index, 1);
    },

    saveAllocation() {
      const bucketId = this.modelValue?.work_bucket_id;
      const timeBucketId = this.modelValue?.id;
      const allocatedDuration = this.totalAllocated;

      if (!bucketId || !timeBucketId) return;

      // Prepare allocations data
      const allocations = this.taskList
        .filter((t) => t.minutes > 0)
        .map((t) => ({
          work_task_id: t.id,
          duration: t.minutes * 60,
          work_task_group_id: t.work_task_group_id,
        }));

      http
        .fetch(
          "/work/time-buckets/allocate",
          {
            time_bucket_id: timeBucketId,
            used_duration: allocatedDuration,
            allocations: allocations,
          },
          true,
          "POST"
        )
        .then(() => {
          this.$emit("saved");
          this.myModal.hide();
          this.$emit("update:modelValue", null);
        });
    },
  },
};
</script>

<style scoped>
.progress-bar {
  font-size: 0.75rem;
}
</style>
